import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiPatch, apiPost } from '../../../services/api';
import { jobToWizardForm, wizardFormToPayload } from './postJobWizardMapping';

const AUTO_SAVE_MS = 30000;

export function usePostJobWizardAutoSave({ token, jobId, setJobId, setForm }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const formRef = useRef(null);
  const jobIdRef = useRef(jobId);
  const idleTimerRef = useRef(null);

  useEffect(() => {
    jobIdRef.current = jobId;
  }, [jobId]);

  const persistDraft = useCallback(
    async (silent = true, formSnapshot) => {
      if (!token || !jobIdRef.current) return false;
      const currentForm = formSnapshot ?? formRef.current;
      if (!currentForm) return false;

      if (!silent) setSaving(true);
      setSaveError('');
      try {
        const res = await apiPatch(
          `/api/v1/company/jobs/${jobIdRef.current}`,
          wizardFormToPayload(currentForm),
          token
        );
        const data = await res.json().catch(() => ({}));
        if (res.status === 401) {
          navigate('/login', { replace: true });
          return false;
        }
        if (!res.ok) {
          if (!silent) setSaveError('Could not save draft. Please try again.');
          return false;
        }
        setLastSavedAt(new Date());
        if (data.data?.id) {
          setForm(jobToWizardForm(data.data));
        }
        return true;
      } catch {
        if (!silent) setSaveError('Could not save draft. Please try again.');
        return false;
      } finally {
        if (!silent) setSaving(false);
      }
    },
    [token, navigate, setForm]
  );

  const ensureJobId = useCallback(async () => {
    if (jobIdRef.current) return jobIdRef.current;
    const res = await apiPost('/api/v1/company/jobs', {}, token);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error('create_failed');
    const id = data.data?.id;
    setJobId(id);
    jobIdRef.current = id;
    setForm(jobToWizardForm(data.data));
    return id;
  }, [token, setJobId, setForm]);

  const bindFormRef = useCallback((form) => {
    formRef.current = form;
  }, []);

  useEffect(() => {
    if (!token) return undefined;

    const onBlur = () => {
      if (jobIdRef.current) persistDraft(true);
    };
    window.addEventListener('blur', onBlur);

    return () => window.removeEventListener('blur', onBlur);
  }, [token, persistDraft]);

  useEffect(() => {
    if (!token || !jobId) return undefined;

    idleTimerRef.current = setInterval(() => {
      persistDraft(true);
    }, AUTO_SAVE_MS);

    return () => {
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
    };
  }, [token, jobId, persistDraft]);

  return {
    persistDraft,
    ensureJobId,
    bindFormRef,
    lastSavedAt,
    saveError,
    saving,
    setSaveError,
    setSaving,
  };
}
